import { Component, Input, Output, EventEmitter, inject, OnChanges, SimpleChanges, OnInit, ChangeDetectorRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { FeatureCardComponent, FeatureCardData } from '../feature-card/feature-card.component';
import { ModelCardComponent, ModelCardData } from '../model-card/model-card.component';
import { UsecaseCardComponent, UsecaseCardData } from '../usecase-card/usecase-card.component';
import {
  ReferenceDatasetCardComponent,
  ReferenceDatasetCardData,
} from '../reference-dataset-card/reference-dataset-card.component';
import { UserInteractionApiService, CatalogueApiService } from '@angular-workspace/services';
import { FormatService } from '../../services/format.service';
import { HelperService } from '../../services/helper.service';
import { GoogleAnalyticsService } from '../../../services/google-analytics.service';
import {
  findHackathonDatasetForCatalogueItem,
  getCatalogueDatasetDetailLink,
  getReferenceDatasetDetailLink,
  HACKATHON_DATASETS,
} from '../../../catalogue/reference-datasets/hackathon-datasets.data';

export type ViewMode = 'grid' | 'list' | 'table';

@Component({
  selector: 'app-asset-grid',
  standalone: true,
  imports: [CommonModule, FormsModule, FeatureCardComponent, ModelCardComponent, UsecaseCardComponent, ReferenceDatasetCardComponent],
  templateUrl: './asset-grid.component.html',
  styleUrl: './asset-grid.component.scss',
  animations: [
    trigger('expandCollapse', [
      transition(':enter', [
        style({ height: '0', opacity: '0', overflow: 'hidden' }),
        animate('200ms ease-out', style({ height: '*', opacity: '1' }))
      ]),
      transition(':leave', [
        style({ height: '*', opacity: '1', overflow: 'hidden' }),
        animate('200ms ease-in', style({ height: '0', opacity: '0' }))
      ])
    ])
  ]
})
export class AssetGridComponent implements OnChanges, OnInit {
  @Input() assets: (FeatureCardData | ModelCardData | UsecaseCardData)[] = [];
  @Input() assetType: 'dataset' | 'model' | 'usecase' = 'dataset';
  @Input() searchPlaceholder: string = 'Search for assets';
  @Input() searchQuery: string = ''; // Changed from property to @Input
  @Input() currentPage: number = 1;
  @Input() totalPages: number = 1;
  @Input() viewMode: ViewMode = 'grid';
  @Input() itemsPerPage: number = 6;
  @Input() showMobileFilterButton: boolean = false;
  /** 'sidebar' = left filter panel; 'popup' = filter icon beside search opens slide-in panel */
  @Input() filterLayout: 'sidebar' | 'popup' = 'sidebar';
  @Input() activeFilterCount: number = 0;
  /** When false, hide the Bookmarks and Like filter toggle buttons (e.g. on Challenges: New / Under Evaluation / Past). */
  @Input() showBookmarkLikeButtons: boolean = true;
  /** When 'reference', render TANUH reference dataset cards instead of feature cards. */
  @Input() datasetCardVariant: 'default' | 'reference' = 'default';
  @Input() isLoggedIn: boolean = false;
  /** When true, show card skeletons instead of empty/results while API loads. */
  @Input() loading: boolean = false;
  /** Number of skeleton cards to show while loading. */
  @Input() skeletonCount: number = 6;

  get skeletonPlaceholders(): number[] {
    return Array.from({ length: Math.max(this.skeletonCount, 1) }, (_, i) => i);
  }
  @Output() pageChange = new EventEmitter<number>();
  @Output() viewModeChange = new EventEmitter<ViewMode>();
  @Output() searchChange = new EventEmitter<string>();
  @Output() itemsPerPageChange = new EventEmitter<number>();
  @Output() openMobileFilter = new EventEmitter<void>();
  @Output() bookmarksClick = new EventEmitter<void>();
  @Output() likeClick = new EventEmitter<void>();
  @Output() referenceDatasetSubmitModel = new EventEmitter<FeatureCardData>();
  @Output() sortChange = new EventEmitter<string>();

  @Input() sortOption: string = 'new-to-old';
  /** When set (e.g. Challenges Under Evaluation / Past), use these options instead of default. */
  @Input() customSortOptions: { value: string; label: string }[] | null = null;

  sortOptions: { value: string; label: string }[] = [
    { value: 'a-z', label: 'A-Z' },
    { value: 'z-a', label: 'Z-A' },
    { value: 'new-to-old', label: 'New to Old' },
    { value: 'old-to-new', label: 'Old to New' },
  ];

  get displaySortOptions(): { value: string; label: string }[] {
    return this.customSortOptions ?? this.sortOptions;
  }

  bookmarksActive: boolean = false;
  likeActive: boolean = false;
  bookmarkedItemIds: string[] = [];
  bookmarksTotalCount: number = 0;
  likedItemIds: string[] = [];
  likesTotalCount: number = 0;
  filteredAssetsFromSearch: (FeatureCardData | ModelCardData | UsecaseCardData)[] = [];
  filteredTotalPages: number = 1; // Track totalPages from search API when filters are active
  _filteredAssets: (FeatureCardData | ModelCardData | UsecaseCardData)[] = []; // Cached filtered assets for better change detection

  private router = inject(Router);
  private userInteractionApiService = inject(UserInteractionApiService);
  private catalogueApiService = inject(CatalogueApiService);
  private formatService = inject(FormatService);
  private helperService = inject(HelperService);
  private googleAnalytics = inject(GoogleAnalyticsService);
  private cdr = inject(ChangeDetectorRef);
  itemsPerPageOptions: number[] = [6, 12, 24, 48];
  expandedRowKeys: { [s: string]: boolean } = {};
  sortDropdownOpen = false;
  showDropdownOpen = false;
  private interactionRefreshInFlight = false;

  ngOnInit(): void {
    // Do not call interaction API on collection page load.
    // Bookmarks are fetched only when user clicks the Bookmarks button (toggleBookmarks).
  }

  ngOnChanges(changes: SimpleChanges): void {
    const interactionFiltersActive = this.bookmarksActive || this.likeActive;
    const itemsPerPageChanged = !!changes['itemsPerPage'];
    const currentPageChanged = !!changes['currentPage'];

    // When interaction filters are active, "Showing" and pagination must re-fetch
    // interaction-filtered results; parent API updates don't update `filteredAssetsFromSearch`.
    if (
      interactionFiltersActive &&
      this.isLoggedIn &&
      (itemsPerPageChanged || currentPageChanged)
    ) {
      void this.refreshInteractionFilteredAssets();
      return;
    }

    // If assets change and filters are active, keep filtered view.
    if (changes['assets'] && interactionFiltersActive) {
      // No need to re-fetch - interaction-filtered list drives the UI.
    }
  }

  /**
   * Refresh BOOKMARK / LIKE filtered list using pagination inputs.
   * This is required because the UI renders from `filteredAssetsFromSearch`
   * (not from parent `assets`) while the toggle filters are active.
   */
  private async refreshInteractionFilteredAssets(): Promise<void> {
    if (!this.isLoggedIn) return;
    if (this.interactionRefreshInFlight) return;

    this.interactionRefreshInFlight = true;
    try {
      const actionType = this.bookmarksActive ? 'BOOKMARK' : 'LIKE';

      // Clear immediately so the user doesn't see stale results.
      this.filteredAssetsFromSearch = [];
      this._filteredAssets = [];
      this.filteredTotalPages = 1;

      const response = await this.userInteractionApiService.userInteractions({
        actionType,
        page: this.currentPage,
        size: this.itemsPerPage,
      });

      if (!response || !response.paginationInfo) {
        if (this.bookmarksActive) {
          this.bookmarksTotalCount = 0;
          this.bookmarkedItemIds = [];
        } else {
          this.likesTotalCount = 0;
          this.likedItemIds = [];
        }
        this.cdr.detectChanges();
        return;
      }

      const totalCount = response.paginationInfo.totalCount || 0;
      const paginationTotalPages = response.paginationInfo.totalPages;

      // Extract item array from response
      let items: any[] = [];
      if (response.result && Array.isArray(response.result)) {
        items = response.result;
      } else if (response.results && Array.isArray(response.results)) {
        items = response.results;
      } else if (response.data && Array.isArray(response.data)) {
        items = response.data;
      }

      if (items.length === 0) {
        if (this.bookmarksActive) {
          this.bookmarksTotalCount = 0;
          this.bookmarkedItemIds = [];
        } else {
          this.likesTotalCount = 0;
          this.likedItemIds = [];
        }
        this.cdr.detectChanges();
        return;
      }

      const assetTypeDisplay =
        this.assetType === 'dataset'
          ? 'Dataset'
          : this.assetType === 'model'
            ? 'Model'
            : this.assetType === 'usecase'
              ? 'Usecase'
              : undefined;

      // Extract asset IDs for ribbons / bookmark / like state
      const assetIds = items
        .map((item: any) => {
          const asset = this.getAssetFromInteractionItem(item);
          if (asset) {
            return asset.id || asset.itemId || asset.assetId || item.assetId || item.itemId || item.id || '';
          }
          return item.assetId || item.itemId || item.id || item.entityId || item.entity_id || '';
        })
        .filter((id: string) => id);

      if (this.bookmarksActive) {
        this.bookmarksTotalCount = totalCount;
        this.bookmarkedItemIds = assetIds;
      } else {
        this.likesTotalCount = totalCount;
        this.likedItemIds = assetIds;
      }

      // If interaction response already includes asset details, map directly.
      const assetsFromInteraction = items
        .map((item: any) => this.getAssetFromInteractionItem(item))
        .filter((a: any) => a != null) as any[];

      if (assetsFromInteraction.length > 0) {
        this.filteredTotalPages =
          paginationTotalPages ??
          (Math.ceil((totalCount || assetsFromInteraction.length) / this.itemsPerPage) || 1);

        this.filteredAssetsFromSearch = assetsFromInteraction
          .map((item, index) => {
            try {
              return this.mapApiItemToCardData(item);
            } catch (error) {
              console.error(
                `Error mapping interaction asset ${index} (${actionType}):`,
                error,
                'Item:',
                item
              );
              return null;
            }
          })
          .filter(
            (item): item is FeatureCardData | ModelCardData | UsecaseCardData => item !== null
          );
      } else if (assetIds.length > 0) {
        // Fallback: fetch asset details by IDs.
        const searchResponse = await this.catalogueApiService.searchAssetsByIds(
          assetIds,
          assetTypeDisplay,
          this.currentPage,
          this.itemsPerPage
        );

        const searchItems = this.extractItemsFromResponse(searchResponse);

        if (searchResponse?.paginationInfo?.totalPages !== undefined) {
          this.filteredTotalPages = searchResponse.paginationInfo.totalPages || 1;
        } else {
          const responseTotal =
            searchResponse?.paginationInfo?.totalCount ||
            searchItems?.length ||
            0;
          this.filteredTotalPages = Math.ceil(responseTotal / this.itemsPerPage) || 1;
        }

        this.filteredAssetsFromSearch = searchItems
          .map((item, index) => {
            try {
              return this.mapApiItemToCardData(item);
            } catch (e) {
              console.error(`Error mapping item ${index} (${actionType}):`, e);
              return null;
            }
          })
          .filter(
            (item): item is FeatureCardData | ModelCardData | UsecaseCardData => item !== null
          );
      }

      this._filteredAssets = [...this.filteredAssetsFromSearch];
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    } catch (error) {
      console.error(`Failed to refresh ${this.bookmarksActive ? 'bookmarks' : 'likes'}:`, error);
      this.filteredAssetsFromSearch = [];
      this._filteredAssets = [];
      this.filteredTotalPages = 1;
      if (this.bookmarksActive) {
        this.bookmarksTotalCount = 0;
        this.bookmarkedItemIds = [];
      } else {
        this.likesTotalCount = 0;
        this.likedItemIds = [];
      }
      this.cdr.detectChanges();
    } finally {
      this.interactionRefreshInFlight = false;

      // Keep existing behavior: emit click events so parent can open login modal if needed.
      if (this.bookmarksActive) this.bookmarksClick.emit();
      if (this.likeActive) this.likeClick.emit();
    }
  }

  /**
   * Load bookmarked items to show ribbons on cards
   */
  async loadBookmarkedItems(): Promise<void> {
    if (!this.isLoggedIn) {
      return;
    }

    try {
      const response = await this.userInteractionApiService.userInteractions({ actionType: 'BOOKMARK' });
      console.log('Bookmarks API response:', response);

      if (response && response.paginationInfo) {
        this.bookmarksTotalCount = response.paginationInfo.totalCount || 0;

        // Extract item IDs from the response - check both 'result' and 'results'
        let items: any[] = [];
        if (response.result && Array.isArray(response.result)) {
          items = response.result;
        } else if (response.results && Array.isArray(response.results)) {
          items = response.results;
        } else if (response.data && Array.isArray(response.data)) {
          items = response.data;
        }

        if (items.length > 0) {
          this.bookmarkedItemIds = items.map((item: any) => {
            // Try multiple possible ID fields
            return item.itemId || item.id || item.entityId || item.entity_id || item.assetId || '';
          }).filter((id: string) => id);
          console.log('Extracted bookmarked item IDs:', this.bookmarkedItemIds);
        } else {
          this.bookmarkedItemIds = [];
        }
      } else {
        this.bookmarksTotalCount = 0;
        this.bookmarkedItemIds = [];
      }
    } catch (error) {
      console.error('Error loading bookmarked items:', error);
      // Silently fail - don't show ribbons if API fails
      this.bookmarkedItemIds = [];
      this.bookmarksTotalCount = 0;
    }
  }

  /**
   * Get filtered assets based on bookmarks or likes state
   */
  get filteredAssets(): (FeatureCardData | ModelCardData | UsecaseCardData)[] {
    // If neither filter is active, show all assets
    if (!this.bookmarksActive && !this.likeActive) {
      this._filteredAssets = this.assets || [];
      return this._filteredAssets;
    }

    // If filter is active but user is not logged in, show all assets
    if (!this.isLoggedIn) {
      this._filteredAssets = this.assets || [];
      return this._filteredAssets;
    }

    // If bookmarks or likes are active, ONLY use assets from search API
    // Do not fallback to filtering existing assets - this ensures we only show
    // the exact results from the API response
    if (this.bookmarksActive || this.likeActive) {
      // Return only the assets from the search API response
      this._filteredAssets = this.filteredAssetsFromSearch || [];
      console.log('filteredAssets getter - bookmarksActive:', this.bookmarksActive, 'likeActive:', this.likeActive, 'filteredAssetsFromSearch length:', this.filteredAssetsFromSearch.length, '_filteredAssets length:', this._filteredAssets.length, 'returning:', this._filteredAssets.length, 'items');
      return this._filteredAssets;
    }

    this._filteredAssets = this.assets || [];
    return this._filteredAssets;
  }

  getEmptyStateMessage(): string {
    const assetTypeName = this.assetType === 'dataset' ? 'Datasets' :
      this.assetType === 'model' ? 'Models' :
        'Usecases';

    if (this.bookmarksActive && this.isLoggedIn) {
      return `No bookmarked ${assetTypeName.toLowerCase()} found.`;
    }

    if (this.likeActive && this.isLoggedIn) {
      return `No liked ${assetTypeName.toLowerCase()} found.`;
    }

    return `No ${assetTypeName} found. Please try with a new search`;
  }

  /**
   * Check if an asset is bookmarked
   */
  isAssetBookmarked(assetId: string | undefined): boolean {
    if (!assetId || !this.isLoggedIn) {
      return false;
    }
    const isBookmarked = this.bookmarkedItemIds.includes(assetId);
    console.log(`Checking bookmark for ${assetId}:`, isBookmarked, 'Bookmarked IDs:', this.bookmarkedItemIds);
    return isBookmarked;
  }

  /**
   * Check if an asset is liked
   */
  isAssetLiked(assetId: string | undefined): boolean {
    if (!assetId || !this.isLoggedIn) {
      return false;
    }
    return this.likedItemIds.includes(assetId);
  }

  /**
   * Handle bookmark state change from feature card
   */
  onBookmarkChanged(assetId: string | undefined, isBookmarked: boolean): void {
    if (!assetId) return;

    if (isBookmarked) {
      // Add to bookmarked items if not already present
      if (!this.bookmarkedItemIds.includes(assetId)) {
        this.bookmarkedItemIds.push(assetId);
        this.bookmarksTotalCount++;
      }
    } else {
      // Remove from bookmarked items
      const index = this.bookmarkedItemIds.indexOf(assetId);
      if (index > -1) {
        this.bookmarkedItemIds.splice(index, 1);
        this.bookmarksTotalCount = Math.max(0, this.bookmarksTotalCount - 1);
      }
    }
  }

  /**
   * Handle like state change from feature card
   */
  onLikeChanged(assetId: string | undefined, isLiked: boolean): void {
    if (!assetId) return;

    if (isLiked) {
      // Add to liked items if not already present
      if (!this.likedItemIds.includes(assetId)) {
        this.likedItemIds.push(assetId);
        this.likesTotalCount++;
      }
    } else {
      // Remove from liked items
      const index = this.likedItemIds.indexOf(assetId);
      if (index > -1) {
        this.likedItemIds.splice(index, 1);
        this.likesTotalCount = Math.max(0, this.likesTotalCount - 1);
      }
    }
  }

  toggleExpansion(assetId: string): void {
    if (this.expandedRowKeys[assetId]) {
      // Collapse
      const newKeys = { ...this.expandedRowKeys };
      delete newKeys[assetId];
      this.expandedRowKeys = newKeys;
    } else {
      // Expand
      this.expandedRowKeys = { ...this.expandedRowKeys, [assetId]: true };
    }
  }

  onSearchChange(): void {
    this.searchChange.emit(this.searchQuery);
  }

  onPageChange(page: number): void {
    const effectiveTotalPages = this.getEffectiveTotalPages();
    if (page >= 1 && page <= effectiveTotalPages) {
      this.pageChange.emit(page);
      // Scroll to top so the user sees the new page content
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  onViewModeChange(mode: ViewMode): void {
    this.viewModeChange.emit(mode);
  }

  onItemsPerPageChange(value: number): void {
    this.itemsPerPage = value;
    this.itemsPerPageChange.emit(value);
  }

  onSortChange(value: string): void {
    this.sortOption = value;
    this.sortChange.emit(value);
  }

  getSelectedSortLabel(): string {
    return this.displaySortOptions.find(o => o.value === this.sortOption)?.label || 'New to Old';
  }

  getSelectedShowLabel(): string {
    return String(this.itemsPerPage);
  }

  toggleSortDropdown(event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();
    this.sortDropdownOpen = !this.sortDropdownOpen;
    if (this.sortDropdownOpen) this.showDropdownOpen = false;
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  toggleShowDropdown(event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();
    this.showDropdownOpen = !this.showDropdownOpen;
    if (this.showDropdownOpen) this.sortDropdownOpen = false;
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  selectSortOption(value: string, event?: Event): void {
    event?.stopPropagation();
    this.sortOption = value;
    this.sortChange.emit(value);
    this.sortDropdownOpen = false;
    this.cdr.markForCheck();
  }

  selectShowOption(value: number, event?: Event): void {
    event?.stopPropagation();
    this.itemsPerPage = value;
    this.itemsPerPageChange.emit(value);
    this.showDropdownOpen = false;
    this.cdr.markForCheck();
  }

  @HostListener('document:click', ['$event'])
  closeDropdownsOnOutsideClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.closest('.asset-grid--custom-dropdown')) return;
    // Defer close to next tick to avoid racing with toggle (change detection)
    setTimeout(() => {
      this.sortDropdownOpen = false;
      this.showDropdownOpen = false;
      this.cdr.markForCheck();
    }, 0);
  }

  get showFilterPopupButton(): boolean {
    return this.filterLayout === 'popup' && this.showMobileFilterButton;
  }

  onMobileFilterClick(): void {
    console.log('Mobile filter button clicked!');
    console.log('showMobileFilterButton:', this.showMobileFilterButton);
    console.log('openMobileFilter emitter:', this.openMobileFilter);
    this.openMobileFilter.emit();
    console.log('Event emitted');
  }

  async toggleBookmarks(): Promise<void> {
    if (!this.isLoggedIn) {
      // If not logged in, emit the click event to trigger login modal
      this.bookmarksClick.emit();
      return;
    }

    // If toggling off, reset state and return
    if (this.bookmarksActive) {
      this.bookmarksActive = false;
      this.bookmarksTotalCount = 0;
      this.bookmarkedItemIds = [];
      this.filteredAssetsFromSearch = [];
      this.filteredTotalPages = 1;
      this.bookmarksClick.emit();
      return;
    }

    // Deactivate like filter when activating bookmarks (mutually exclusive)
    if (this.likeActive) {
      this.likeActive = false;
      this.likesTotalCount = 0;
      this.likedItemIds = [];
      this.filteredAssetsFromSearch = [];
      this.filteredTotalPages = 1;
    }

    // Set active state and clear results IMMEDIATELY (before API call)
    // This ensures the UI updates right away instead of showing old cards
    this.bookmarksActive = true;
    this.filteredAssetsFromSearch = []; // Clear immediately to show empty state while loading

    // If toggling on, call userInteractions API
    try {
      const response = await this.userInteractionApiService.userInteractions({
        actionType: 'BOOKMARK',
        page: this.currentPage,
        size: this.itemsPerPage
      });

      // Extract bookmarked item IDs and total count from response
      if (response && response.paginationInfo) {
        this.bookmarksTotalCount = response.paginationInfo.totalCount || 0;

        // Extract item IDs from the response - check multiple possible response structures
        let items: any[] = [];
        if (response.result && Array.isArray(response.result)) {
          items = response.result;
        } else if (response.results && Array.isArray(response.results)) {
          items = response.results;
        } else if (response.data && Array.isArray(response.data)) {
          items = response.data;
        }

        if (items.length > 0) {
          // Extract asset IDs for bookmark ribbons
          const assetIds = items.map((item: any) => {
            const asset = this.getAssetFromInteractionItem(item);
            if (asset) return asset.id || asset.itemId || asset.assetId || item.assetId || item.itemId || item.id || '';
            return item.assetId || item.itemId || item.id || item.entityId || item.entity_id || '';
          }).filter((id: string) => id);
          this.bookmarkedItemIds = assetIds;

          // Use asset information from interaction response when available (no item API call)
          const assetsFromInteraction = items
            .map((item: any) => this.getAssetFromInteractionItem(item))
            .filter((a: any) => a != null) as any[];

          if (assetsFromInteraction.length > 0) {
            // Interaction API returned full asset info – map directly to cards
            try {
              this.filteredTotalPages = response.paginationInfo?.totalPages ?? (Math.ceil((response.paginationInfo?.totalCount ?? assetsFromInteraction.length) / this.itemsPerPage) || 1);
              this.filteredAssetsFromSearch = assetsFromInteraction.map((item, index) => {
                try {
                  return this.mapApiItemToCardData(item);
                } catch (error) {
                  console.error(`Error mapping interaction asset ${index}:`, error, 'Item:', item);
                  return null;
                }
              }).filter((item): item is FeatureCardData | ModelCardData | UsecaseCardData => item !== null);
              this._filteredAssets = [...this.filteredAssetsFromSearch];
              this.cdr.markForCheck();
              this.cdr.detectChanges();
              setTimeout(() => { this.cdr.markForCheck(); this.cdr.detectChanges(); }, 0);
            } catch (error) {
              console.error('Error mapping interaction assets to card data:', error);
              this.filteredAssetsFromSearch = [];
              this._filteredAssets = [];
              this.filteredTotalPages = 1;
              this.cdr.detectChanges();
            }
          } else {
            // Fallback: call search API by IDs when interaction does not include asset details
            if (assetIds.length > 0) {
              try {
                const assetTypeDisplay = this.assetType === 'dataset' ? 'Dataset' : this.assetType === 'model' ? 'Model' : this.assetType === 'usecase' ? 'Usecase' : undefined;
                const searchResponse = await this.catalogueApiService.searchAssetsByIds(assetIds, assetTypeDisplay, this.currentPage, this.itemsPerPage);
                const searchItems = this.extractItemsFromResponse(searchResponse);
                if (searchResponse?.paginationInfo) {
                  this.filteredTotalPages = searchResponse.paginationInfo.totalPages || 1;
                } else {
                  const totalCount = searchResponse?.paginationInfo?.totalCount || searchItems?.length || 0;
                  this.filteredTotalPages = Math.ceil(totalCount / this.itemsPerPage) || 1;
                }
                this.filteredAssetsFromSearch = searchItems.map((item, index) => {
                  try { return this.mapApiItemToCardData(item); } catch (e) { console.error(`Error mapping item ${index}:`, e); return null; }
                }).filter(item => item !== null) as (FeatureCardData | ModelCardData | UsecaseCardData)[];
                this._filteredAssets = [...this.filteredAssetsFromSearch];
                this.cdr.markForCheck();
                this.cdr.detectChanges();
                setTimeout(() => { this.cdr.markForCheck(); this.cdr.detectChanges(); }, 0);
              } catch (error) {
                console.error('Error fetching bookmarked assets:', error);
                this.filteredAssetsFromSearch = [];
                this.filteredTotalPages = 1;
              }
            } else {
              this.filteredAssetsFromSearch = [];
            }
          }
        } else {
          this.bookmarkedItemIds = [];
          this.filteredAssetsFromSearch = [];
          this.filteredTotalPages = 1;
          this.cdr.detectChanges();
        }
      } else {
        this.bookmarksTotalCount = 0;
        this.bookmarkedItemIds = [];
        this.filteredAssetsFromSearch = [];
        this.filteredTotalPages = 1;
        this.cdr.detectChanges();
      }

      // Emit event after successful API call (bookmarksActive was already set above)
      this.bookmarksClick.emit();
    } catch (error) {
      console.error('Error calling userInteractions API:', error);
      // Reset bookmarks state on error
      this.bookmarksTotalCount = 0;
      this.bookmarkedItemIds = [];
      this.bookmarksActive = false;
      // Still emit the event even if API call fails
      this.bookmarksClick.emit();
    }
  }

  async toggleLike(): Promise<void> {
    if (!this.isLoggedIn) {
      // If not logged in, emit the click event to trigger login modal
      this.likeClick.emit();
      return;
    }

    // If toggling off, reset state and return
    if (this.likeActive) {
      this.likeActive = false;
      this.likesTotalCount = 0;
      this.likedItemIds = [];
      this.filteredAssetsFromSearch = [];
      this.filteredTotalPages = 1;
      this.likeClick.emit();
      return;
    }

    // Deactivate bookmark filter when activating likes (mutually exclusive)
    if (this.bookmarksActive) {
      this.bookmarksActive = false;
      this.bookmarksTotalCount = 0;
      this.bookmarkedItemIds = [];
      this.filteredAssetsFromSearch = [];
      this.filteredTotalPages = 1;
    }

    // Set active state and clear results IMMEDIATELY (before API call)
    // This ensures the UI updates right away instead of showing old cards
    this.likeActive = true;
    this.filteredAssetsFromSearch = []; // Clear immediately to show empty state while loading

    // If toggling on, call userInteractions API
    try {
      const response = await this.userInteractionApiService.userInteractions({
        actionType: 'LIKE',
        page: this.currentPage,
        size: this.itemsPerPage
      });

      // Extract liked item IDs and total count from response
      if (response && response.paginationInfo) {
        this.likesTotalCount = response.paginationInfo.totalCount || 0;

        // Extract item IDs from the response - check multiple possible response structures
        let items: any[] = [];
        if (response.result && Array.isArray(response.result)) {
          items = response.result;
        } else if (response.results && Array.isArray(response.results)) {
          items = response.results;
        } else if (response.data && Array.isArray(response.data)) {
          items = response.data;
        }

        if (items.length > 0) {
          // Extract asset IDs for like ribbons
          const assetIds = items.map((item: any) => {
            const asset = this.getAssetFromInteractionItem(item);
            if (asset) return asset.id || asset.itemId || asset.assetId || item.assetId || item.itemId || item.id || '';
            return item.assetId || item.itemId || item.id || item.entityId || item.entity_id || '';
          }).filter((id: string) => id);
          this.likedItemIds = assetIds;

          // Use asset information from interaction response when available (no item API call)
          const assetsFromInteraction = items
            .map((item: any) => this.getAssetFromInteractionItem(item))
            .filter((a: any) => a != null) as any[];

          if (assetsFromInteraction.length > 0) {
            // Interaction API returned full asset info – map directly to cards
            try {
              this.filteredTotalPages = response.paginationInfo?.totalPages ?? (Math.ceil((response.paginationInfo?.totalCount ?? assetsFromInteraction.length) / this.itemsPerPage) || 1);
              this.filteredAssetsFromSearch = assetsFromInteraction.map((item, index) => {
                try {
                  return this.mapApiItemToCardData(item);
                } catch (error) {
                  console.error(`Error mapping interaction asset ${index} for likes:`, error, 'Item:', item);
                  return null;
                }
              }).filter((item): item is FeatureCardData | ModelCardData | UsecaseCardData => item !== null);
              this._filteredAssets = [...this.filteredAssetsFromSearch];
              this.cdr.markForCheck();
              this.cdr.detectChanges();
              setTimeout(() => { this.cdr.markForCheck(); this.cdr.detectChanges(); }, 0);
            } catch (error) {
              console.error('Error mapping interaction assets to card data for likes:', error);
              this.filteredAssetsFromSearch = [];
              this._filteredAssets = [];
              this.filteredTotalPages = 1;
              this.cdr.detectChanges();
            }
          } else {
            // Fallback: call search API by IDs when interaction does not include asset details
            if (assetIds.length > 0) {
              try {
                const assetTypeDisplay = this.assetType === 'dataset' ? 'Dataset' : this.assetType === 'model' ? 'Model' : this.assetType === 'usecase' ? 'Usecase' : undefined;
                const searchResponse = await this.catalogueApiService.searchAssetsByIds(assetIds, assetTypeDisplay, this.currentPage, this.itemsPerPage);
                const searchItems = this.extractItemsFromResponse(searchResponse);
                if (searchResponse?.paginationInfo) {
                  this.filteredTotalPages = searchResponse.paginationInfo.totalPages || 1;
                } else {
                  const totalCount = searchResponse?.paginationInfo?.totalCount || searchItems?.length || 0;
                  this.filteredTotalPages = Math.ceil(totalCount / this.itemsPerPage) || 1;
                }
                this.filteredAssetsFromSearch = searchItems.map((item, index) => {
                  try { return this.mapApiItemToCardData(item); } catch (e) { console.error(`Error mapping item ${index} for likes:`, e); return null; }
                }).filter(item => item !== null) as (FeatureCardData | ModelCardData | UsecaseCardData)[];
                this._filteredAssets = [...this.filteredAssetsFromSearch];
                this.cdr.markForCheck();
                this.cdr.detectChanges();
                setTimeout(() => { this.cdr.markForCheck(); this.cdr.detectChanges(); }, 0);
              } catch (error) {
                console.error('Error fetching liked assets:', error);
                this.filteredAssetsFromSearch = [];
                this.filteredTotalPages = 1;
              }
            } else {
              this.filteredAssetsFromSearch = [];
            }
          }
        } else {
          this.likedItemIds = [];
          this.filteredAssetsFromSearch = [];
          this.filteredTotalPages = 1;
          this.cdr.detectChanges();
        }
      } else {
        this.likesTotalCount = 0;
        this.likedItemIds = [];
        this.filteredAssetsFromSearch = [];
        this.filteredTotalPages = 1;
        this.cdr.detectChanges();
      }

      this.likeClick.emit();
    } catch (error) {
      console.error('Error calling userInteractions API for likes:', error);
      // Reset like state on error
      this.likesTotalCount = 0;
      this.likedItemIds = [];
      this.likeActive = false;
      // Still emit the event even if API call fails
      this.likeClick.emit();
    }
  }

  /**
   * Get effective total pages based on whether filters are active
   */
  getEffectiveTotalPages(): number {
    // If bookmarks or likes are active, use filteredTotalPages
    if (this.bookmarksActive || this.likeActive) {
      return this.filteredTotalPages;
    }
    // Otherwise, use the regular totalPages from parent
    return this.totalPages;
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisible = 5;
    const effectiveTotalPages = this.getEffectiveTotalPages();
    let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(effectiveTotalPages, start + maxVisible - 1);

    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }

  /**
   * Check if badge color is restricted
   */
  isRestrictedBadge(badgeColor?: string): boolean {
    return badgeColor === 'restricted';
  }

  /**
   * Check if badge color is public
   */
  isPublicBadge(badgeColor?: string): boolean {
    return badgeColor === 'public';
  }

  onActionClick(asset: FeatureCardData | ModelCardData | UsecaseCardData): void {
    if (!asset.id) {
      console.warn('Asset ID is missing, cannot navigate to detail page');
      return;
    }

    if (this.assetType === 'dataset') {
      this.navigateToDatasetDetail(asset as FeatureCardData);
      return;
    }

    console.log('Navigating to detail page for asset:', asset.id, 'Type:', this.assetType);

    if (this.assetType === 'model') {
      const title = (asset as ModelCardData).title ?? '';
      this.googleAnalytics.trackModelClick(title, asset.id);
      this.router.navigate(['/model', asset.id]);
    } else if (this.assetType === 'usecase') {
      this.router.navigate(['/usecase', asset.id]);
    }
  }

  toReferenceDatasetCardData(asset: FeatureCardData): ReferenceDatasetCardData {
    const referenceDataset = asset.referenceDatasetKey
      ? HACKATHON_DATASETS.find((dataset) => dataset.id === asset.referenceDatasetKey)
      : undefined;
    const primaryMetrics = referenceDataset
      ? [referenceDataset.primaryMetric, referenceDataset.secondPrimaryMetric]
      : [asset.primaryMetric, asset.secondPrimaryMetric].filter(
          (metric): metric is string => !!metric && metric !== '—',
        );

    return {
      id: asset.id,
      title: asset.title,
      problemStatement: asset.problemStatement,
      modality: asset.modality ?? '—',
      volume: asset.volume ?? '—',
      output: asset.output ?? '—',
      primaryMetrics: primaryMetrics.length > 0 ? primaryMetrics : ['—'],
      secondaryMetric: asset.secondaryMetric ?? '—',
    };
  }

  onReferenceDatasetViewDetails(asset: FeatureCardData): void {
    this.navigateToDatasetDetail(asset);
  }

  private navigateToDatasetDetail(asset: FeatureCardData): void {
    if (!asset.id) {
      return;
    }

    const title = asset.title ?? '';
    const from = this.datasetCardVariant === 'reference' ? 'reference' : 'datasets';

    if (asset.link?.startsWith('/reference/dataset/')) {
      this.googleAnalytics.trackDatasetClick(title, asset.id);
      void this.router.navigateByUrl(asset.link);
      return;
    }

    if (asset.referenceDatasetKey) {
      const ds = HACKATHON_DATASETS.find((d) => d.id === asset.referenceDatasetKey);
      if (ds) {
        this.googleAnalytics.trackDatasetClick(title, asset.id);
        void this.router.navigateByUrl(getReferenceDatasetDetailLink(ds, from));
        return;
      }
    }

    const matched = findHackathonDatasetForCatalogueItem(asset);
    if (matched) {
      this.googleAnalytics.trackDatasetClick(title, asset.id);
      void this.router.navigateByUrl(getReferenceDatasetDetailLink(matched, from));
      return;
    }

    this.googleAnalytics.trackDatasetClick(title, asset.id);
    void this.router.navigate(['/dataset', asset.id], {
      queryParams: { from },
    });
  }

  onReferenceDatasetSubmitModel(asset: FeatureCardData): void {
    this.referenceDatasetSubmitModel.emit(asset);
  }

  getDisplayTag(tag: string): string {
    // Replace PII with Personal for UI display only (case-insensitive)
    if (!tag) return tag;
    return tag.toUpperCase() === 'PII' ? 'Personal' : tag;
  }

  /**
   * Extract asset (catalogue item) from a user interaction API item.
   * The interaction API may return full asset info in assetDetails, item, asset, or the item itself.
   */
  private getAssetFromInteractionItem(item: any): any | null {
    if (!item || typeof item !== 'object') return null;
    const asset = item.assetDetails ?? item.item ?? item.asset ?? item;
    if (!asset || typeof asset !== 'object') return null;
    const hasId = asset.id != null || asset.itemId != null || asset.assetId != null || asset.entityId != null;
    const hasName = (asset.name != null && asset.name !== '') || (asset.label != null && asset.label !== '') || (asset.title != null && asset.title !== '');
    if (hasId || hasName) return asset;
    return null;
  }

  /**
   * Extract items array from API response
   */
  private extractItemsFromResponse(response: any): any[] {
    // Handle null/undefined response
    if (!response) {
      console.warn('Response is null or undefined');
      return [];
    }

    console.log('Extracting items from response. Response keys:', Object.keys(response));

    // Direct array response
    if (Array.isArray(response)) {
      console.log('Response is direct array, length:', response.length);
      return response;
    }

    // Check for result array (most common structure: { result: [...], paginationInfo: {...} })
    if (response.result) {
      console.log('Found response.result:', response.result);
      if (Array.isArray(response.result)) {
        console.log('response.result is array, length:', response.result.length);
        return response.result;
      }
      // Handle nested result.results
      if (response.result.results && Array.isArray(response.result.results)) {
        console.log('Found response.result.results, length:', response.result.results.length);
        return response.result.results;
      }
    }

    // Check for data array
    if (response.data && Array.isArray(response.data)) {
      console.log('Found response.data array, length:', response.data.length);
      return response.data;
    }

    // Check for items array
    if (response.items && Array.isArray(response.items)) {
      console.log('Found response.items array, length:', response.items.length);
      return response.items;
    }

    // Log warning if no items found
    console.warn('No items array found in response. Response structure:', Object.keys(response));
    console.warn('Full response:', JSON.stringify(response, null, 2));
    return [];
  }

  /**
   * Map API item to card data format based on asset type
   */
  private mapApiItemToCardData(item: any): FeatureCardData | ModelCardData | UsecaseCardData {
    if (this.assetType === 'dataset') {
      return this.mapApiItemToFeatureCard(item);
    } else if (this.assetType === 'model') {
      return this.mapApiItemToModelCard(item);
    } else {
      return this.mapApiItemToUsecaseCard(item);
    }
  }

  /**
   * Map API item to FeatureCardData format (for datasets)
   */
  private mapApiItemToFeatureCard(item: any): FeatureCardData {
    const id = item.id || '';
    const title = item.label || item.name || 'Untitled';
    const organization = item.organizationName || item.provider || item.organisation || item.organization || item.provider_name || item.uploadedBy || '';
    // Short description: prefer shortDescription, else truncate description (e.g. 120 chars)
    const rawDesc = item.shortDescription ?? item.short_description ?? item.sortDescription ?? item.description ?? '';
    const shortDescription = typeof rawDesc === 'string' && rawDesc.trim()
      ? (rawDesc.length > 120 ? rawDesc.trim().slice(0, 120) + '…' : rawDesc.trim())
      : undefined;

    // Extract ResourceServer names for card display
    const resourceServerTags: string[] = [];
    if (item.resourceServer) {
      if (Array.isArray(item.resourceServer)) {
        item.resourceServer.forEach((server: any) => {
          if (server.name && !resourceServerTags.includes(server.name.toUpperCase())) {
            resourceServerTags.push(server.name.toUpperCase());
          }
          if (server.type && !resourceServerTags.includes(server.type.toUpperCase())) {
            resourceServerTags.push(server.type.toUpperCase());
          }
        });
      } else if (item.resourceServer.name) {
        resourceServerTags.push(item.resourceServer.name.toUpperCase());
      } else if (item.resourceServer.type) {
        resourceServerTags.push(item.resourceServer.type.toUpperCase());
      }
    }

    // Extract actual tags from API
    const apiTags: string[] = [];
    if (item.tags && Array.isArray(item.tags)) {
      apiTags.push(...item.tags);
    }

    const percentage = item.percentage || item.dataReadiness || undefined;
    const fileFormat = item.fileFormat || item.format || '';
    const fileType = fileFormat || '';
    const date = this.formatService.formatDateForCard(item.itemCreatedAt || item.createdAt || new Date());
    const badge = this.helperService.extractAccessBadge(item);
    const badgeColor = this.helperService.getBadgeColorVariant(badge);
    const isNew = this.isRecentItem(item.itemCreatedAt || item.createdAt);
    const referenceDataset = findHackathonDatasetForCatalogueItem(item);
    const link = referenceDataset
      ? getReferenceDatasetDetailLink(referenceDataset, 'datasets')
      : getCatalogueDatasetDetailLink(id, 'datasets');

    // Extract metrics data from metrics object
    const metricsData = item.metrics || {};
    const metrics = {
      likes: metricsData.likes ?? item.likeCount ?? item.likes ?? item.like_count ?? 0,
      downloads: metricsData.downloads ?? item.downloadCount ?? item.downloads ?? item.download_count ?? 0,
      files: item.fileCount || item.files || item.file_count || 0,
      views: metricsData.views ?? item.viewCount ?? item.views ?? item.view_count ?? 0,
      dislikes: metricsData.dislikes ?? 0
    };

    return {
      id,
      title,
      shortDescription,
      organization,
      tags: resourceServerTags.slice(0, 3),
      apiTags: apiTags,
      percentage,
      fileType,
      date,
      badge,
      badgeColor,
      isNew,
      link,
      ...(referenceDataset
        ? {
            modality: referenceDataset.modality,
            volume: referenceDataset.volume,
            output: referenceDataset.output,
            primaryMetric: referenceDataset.primaryMetric,
            secondPrimaryMetric: referenceDataset.secondPrimaryMetric,
            secondaryMetric: referenceDataset.secondaryMetric,
            referenceDatasetKey: referenceDataset.id,
          }
        : {
            modality: item.modality || undefined,
            volume: item.volume != null ? String(item.volume) : undefined,
            output: item.output || undefined,
            primaryMetric: item.primaryMetric || undefined,
            secondaryMetric: item.secondaryMetric || undefined,
          }),
      metrics: metrics
    };
  }

  /**
   * Map API item to ModelCardData format (for models)
   */
  private mapApiItemToModelCard(item: any): ModelCardData {
    const id = item.id || '';
    const title = item.label || item.name || 'Untitled';
    const organization = item.organizationName || item.provider || item.organisation || item.organization || item.provider_name || item.uploadedBy || '';
    const rawDesc = item.shortDescription ?? item.short_description ?? item.description ?? '';
    const shortDescription = typeof rawDesc === 'string' && rawDesc.trim()
      ? (rawDesc.length > 120 ? rawDesc.trim().slice(0, 120) + '…' : rawDesc.trim())
      : undefined;

    // Extract tags from API
    const apiTags: string[] = [];
    if (item.tags && Array.isArray(item.tags)) {
      apiTags.push(...item.tags);
    }

    // Extract industry
    let industry = '';
    if (item.industry) {
      if (Array.isArray(item.industry)) {
        industry = item.industry.filter((v: any) => v !== null && v !== undefined && v !== '').join(', ');
      } else if (typeof item.industry === 'string' && item.industry.trim() !== '') {
        industry = item.industry.trim();
      }
    }

    const modelType = item.modelType || '';
    const language = item.codeLanguage || item.fileFormat || item.format || '';
    const date = this.formatService.formatDateForModelCard(item.itemCreatedAt || item.createdAt || new Date());
    const badge = this.helperService.extractAccessBadge(item);
    const badgeColor = this.helperService.getBadgeColorVariant(badge);
    const link = `/model/${id}`;

    // Extract metrics data from metrics object
    const metricsData = item.metrics || {};
    const metrics = {
      likes: metricsData.likes ?? item.likeCount ?? item.likes ?? item.like_count ?? 0,
      downloads: metricsData.downloads ?? item.downloadCount ?? item.downloads ?? item.download_count ?? 0,
      files: item.fileCount || item.files || item.file_count || 0,
      views: metricsData.views ?? item.viewCount ?? item.views ?? item.view_count ?? 0,
      dislikes: metricsData.dislikes ?? 0
    };

    return {
      id,
      title,
      organization,
      shortDescription,
      industry,
      tags: apiTags.slice(0, 3),
      modelType,
      language,
      date,
      badge,
      badgeColor,
      link,
      metrics: metrics
    };
  }

  /**
   * Map API item to UsecaseCardData format (for usecases)
   */
  private mapApiItemToUsecaseCard(item: any): UsecaseCardData {
    const id = item.id || '';
    const title = item.label || item.name || 'Untitled';
    // Support multiple API field names (interaction API may use shortDescription, sortDescription, etc.)
    const description =
      (item.description ?? item.shortDescription ?? item.short_description ?? item.longDescription ?? item.long_description ?? item.sortDescription ?? '') || '';

    // Extract tags from API
    const apiTags: string[] = [];
    if (item.tags && Array.isArray(item.tags)) {
      apiTags.push(...item.tags);
    }

    const date = this.formatService.formatDateForCard(item.itemCreatedAt || item.createdAt || new Date());
    const badge = this.helperService.extractAccessBadge(item);
    const badgeColor = this.helperService.getBadgeColorVariant(badge);
    const isNew = this.isRecentItem(item.itemCreatedAt || item.createdAt);
    const link = `/usecase/${id}`;

    // Extract metrics data from metrics object
    const metricsData = item.metrics || {};
    const metrics = {
      likes: metricsData.likes ?? item.likeCount ?? item.likes ?? item.like_count ?? 0,
      downloads: metricsData.downloads ?? item.downloadCount ?? item.downloads ?? item.download_count ?? 0,
      files: item.fileCount || item.files || item.file_count || 0,
      views: metricsData.views ?? item.viewCount ?? item.views ?? item.view_count ?? 0,
      dislikes: metricsData.dislikes ?? 0
    };

    return {
      id,
      title,
      description,
      tags: apiTags.slice(0, 4),
      apiTags: apiTags,
      badge,
      badgeColor,
      isNew,
      link,
      metrics: metrics
    };
  }

  /**
   * Check if item was created within last 7 days
   */
  private isRecentItem(dateString: string | Date): boolean {
    if (!dateString) return false;
    const itemDate = new Date(dateString);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return itemDate >= weekAgo;
  }

}

